/**
 * CSV for the console's exports — built in memory from a live answer, handed to the browser, never stored.
 *
 * RFC 4180 quoting (a comma, quote, CR or LF wraps the cell in quotes and doubles the quotes). A TEXT cell that a
 * spreadsheet would run as a formula (`=`, `+`, `-`, `@`, tab, CR first) gets a leading `'` — tenant names and gateway
 * labels come from customers. Numbers are written as numbers, so a negative amount stays a number.
 */
export type CsvCell = string | number | boolean | null | undefined;

const FORMULA_START = /^[=+\-@\t\r]/;
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

export function csvCell(value: CsvCell): string {
  if (value === null || value === undefined) return '';
  let text = String(value);
  if (typeof value === 'string' && FORMULA_START.test(text) && !PLAIN_NUMBER.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Rows (the first one is usually the header) → CSV text, `\n` line ends. */
export function toCsv(rows: readonly (readonly CsvCell[])[]): string {
  return rows.map((row) => row.map(csvCell).join(',')).join('\n');
}

/** Hands a CSV string to the browser as a download. */
export function downloadCsv(name: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
